import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, CheckCircle2, Loader2, IdCard, FileText } from "lucide-react";

const ICONS = { id_document: IdCard, proof_of_address: FileText };

export function DocumentsCard() {
  const [docs, setDocs] = useState(null);
  const [labels, setLabels] = useState({});
  const [uploading, setUploading] = useState(null);
  const inputs = useRef({});

  const load = async () => {
    const { data } = await api.get("/documents");
    setDocs(data.documents);
    setLabels(data.labels);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount

  const onFile = async (docType, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("File too large (max 8MB)"); return; }
    setUploading(docType);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/documents/${docType}`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      await load();
    } catch (e) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(null);
      if (inputs.current[docType]) inputs.current[docType].value = "";
    }
  };

  return (
    <div className="mt-6 bg-white rounded-xl border border-brand-line p-6 md:p-8 shadow-sm" data-testid="documents-card">
      <h2 className="font-serif text-2xl text-brand-green">Verification documents</h2>
      <p className="text-sm text-brand-muted mt-1 mb-5">Please upload a photo ID and a recent proof of address (max 8MB each — PDF, JPG or PNG).</p>
      <div className="grid sm:grid-cols-2 gap-4">
        {Object.keys(labels).map((dt) => {
          const Icon = ICONS[dt] || FileText;
          const uploaded = docs?.[dt];
          return (
            <div key={dt} className="rounded-lg border border-brand-line p-4 flex flex-col" data-testid={`doc-${dt}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-9 h-9 rounded-lg bg-brand-admin text-brand-green flex items-center justify-center"><Icon className="w-4 h-4" /></span>
                <span className="font-semibold text-brand-ink text-sm">{labels[dt]}</span>
              </div>
              {uploaded ? (
                <p className="text-sm text-brand-success flex items-center gap-1.5 mb-3" data-testid={`doc-${dt}-status`}>
                  <CheckCircle2 className="w-4 h-4" /> {uploaded.filename}
                </p>
              ) : (
                <p className="text-sm text-brand-muted mb-3" data-testid={`doc-${dt}-status`}>Not uploaded yet</p>
              )}
              <input ref={(el) => (inputs.current[dt] = el)} type="file" accept=".pdf,.jpg,.jpeg,.png,image/*,application/pdf"
                className="hidden" onChange={(e) => onFile(dt, e.target.files?.[0])} data-testid={`doc-${dt}-input`} />
              <Button variant="outline" size="sm" disabled={uploading === dt}
                onClick={() => inputs.current[dt]?.click()} data-testid={`doc-${dt}-upload`}
                className="mt-auto self-start border-brand-line text-brand-green hover:bg-brand-admin">
                {uploading === dt ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                {uploaded ? "Replace" : "Upload"}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
